import { applyPriceOp, formatARS, roundToStep } from '@gl/shared';
import { DollarSign, Percent, Tag } from 'lucide-react';
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/ui/input';
import { ChipGroup, OptionCards } from '../ui/option-cards';
import { toBulkBody, wizardSchema, type WizardValues } from '../wizard-form';

/** "Un producto de $ 6.500 queda en $ 7.475" con la operación (y el redondeo si `withRounding`). */
export function exampleText(values: WizardValues, sampleCents: number, withRounding: boolean): string | null {
  if (!wizardSchema.safeParse(values).success) return null;
  const body = toBulkBody(values);
  let next = applyPriceOp(sampleCents, body.op);
  if (withRounding && body.rounding?.mode && body.rounding.mode !== 'NONE' && body.rounding.stepCents) {
    next = roundToStep(next, body.rounding.stepCents, body.rounding.mode);
  }
  return `Ej.: un producto de ${formatARS(sampleCents)} queda en ${formatARS(next)}`;
}

export function OperationStep({ form, sampleCents }: { form: UseFormReturn<WizardValues>; sampleCents: number }) {
  const values = useWatch({ control: form.control }) as WizardValues;
  const { errors } = form.formState;
  const isPercent = values.opType === 'PERCENT';
  const example = exampleText(values, sampleCents, false);

  return (
    <div className="grid gap-5">
      <Controller
        control={form.control}
        name="opType"
        render={({ field }) => (
          <OptionCards
            name="Operación"
            value={field.value}
            onChange={(v) => {
              field.onChange(v);
              form.setValue('amount', '');
              form.clearErrors('amount');
            }}
            options={[
              { value: 'PERCENT', label: 'Porcentaje', description: 'Ej.: subir 15 %', icon: <Percent /> },
              { value: 'DELTA_CENTS', label: 'Monto fijo', description: 'Ej.: sumar $ 500 a cada precio', icon: <DollarSign /> },
              { value: 'SET_CENTS', label: 'Precio fijo', description: 'Todos quedan con el mismo precio', icon: <Tag /> },
            ]}
          />
        )}
      />

      {values.opType !== 'SET_CENTS' && (
        <Controller
          control={form.control}
          name="direction"
          render={({ field }) => (
            <ChipGroup
              name="Sentido"
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: 'UP', label: 'Subir' },
                { value: 'DOWN', label: 'Bajar' },
              ]}
            />
          )}
        />
      )}

      <FormField
        label={isPercent ? 'Porcentaje' : values.opType === 'DELTA_CENTS' ? 'Monto a sumar o restar' : 'Precio nuevo'}
        htmlFor="price-amount"
        error={errors.amount?.message}
        hint={example ?? undefined}
      >
        <div className="relative max-w-xs">
          {!isPercent && (
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground">$</span>
          )}
          <Input
            id="price-amount"
            inputMode="decimal"
            autoComplete="off"
            placeholder={isPercent ? '15' : '1.500'}
            className={isPercent ? 'pr-8' : 'pl-7'}
            aria-invalid={!!errors.amount}
            {...form.register('amount')}
          />
          {isPercent && (
            <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground">%</span>
          )}
        </div>
      </FormField>
    </div>
  );
}
